import { ServiceType } from '@/generated/graphql'
import { sortBy } from 'lodash'

type DeliveryFeeLike =
  | {
      total?: number | null
      totalExTax?: number | null
    }
  | null
  | undefined

type DeliveryRegionLike =
  | {
      serviceType?: ServiceType | null
      orderPosition?: number | null
      minOrderValueExTax?: number | null
      deliveryFee?: DeliveryFeeLike
    }
  | null
  | undefined

type InRangeLocationLike = {
  details?: {
    effectiveMinimumNoticePeriod?: number | null
  } | null
  deliveryRegions?: Array<DeliveryRegionLike> | null
} | null

type VendorLocationLike = {
  isDefault?: boolean | null
  deliveryRegions?: Array<DeliveryRegionLike> | null
} | null

type VendorLike = {
  inRangeLocations?: Array<InRangeLocationLike> | null
  locations?: Array<VendorLocationLike> | null
}

export type DeliverySummary = {
  deliveryFee?: number | null
  minOrderFee?: number | null
  minNoticePeriod?: number | null
}

const pickPreferredRegion = (
  regions?: Array<DeliveryRegionLike> | null,
): NonNullable<DeliveryRegionLike> | null => {
  if (!regions || !regions.length) return null
  const sortedRegions = sortBy(
    regions.filter((region): region is NonNullable<DeliveryRegionLike> => Boolean(region)),
    (region) => region.orderPosition ?? Number.MAX_SAFE_INTEGER,
  )
  if (!sortedRegions.length) return null
  return (
    sortedRegions.find((region) => region.serviceType === ServiceType.Gm) ??
    sortedRegions[0] ??
    null
  )
}

export const getDeliverySummaryFromVendor = (vendor: VendorLike): DeliverySummary | undefined => {
  const inRangeLocation = vendor.inRangeLocations?.find(Boolean)
  const primaryRegion = pickPreferredRegion(inRangeLocation?.deliveryRegions)
  const fallbackRegion = pickPreferredRegion(
    vendor.locations?.find((location) => location?.isDefault)?.deliveryRegions,
  )
  const region = primaryRegion ?? fallbackRegion

  if (!region) return undefined

  return {
    deliveryFee: region.deliveryFee?.totalExTax ?? null,
    minOrderFee: region.minOrderValueExTax ?? null,
    minNoticePeriod: inRangeLocation?.details?.effectiveMinimumNoticePeriod ?? null,
  }
}
